import React, { useState, useEffect, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { PercentSquare, Calculator, FileText, Info } from 'lucide-react';

import Header from '../../../components/layout/Header';
import Footer from '../../../components/layout/Footer';
import Breadcrumbs from '../../../components/ui/Breadcrumbs';
import ToolSidebar from '../../../components/layout/ToolSidebar';
import { useTheme } from '../../../contexts/ThemeContext';
import { getToolById } from '../../../data/tools';

// Импорт компонентов калькулятора
import CalculationForm from './TaxDeductionCalculatorPage/components/CalculationForm';
import ResultsOverview from './TaxDeductionCalculatorPage/components/ResultsOverview';
import DeductionsSchedule from './TaxDeductionCalculatorPage/components/DeductionsSchedule';
import QuickAdjustmentSidebar from './TaxDeductionCalculatorPage/components/QuickAdjustmentSidebar';
import ComparisonSidebar from './TaxDeductionCalculatorPage/components/ComparisonSidebar';
import TipsSidebar from './TaxDeductionCalculatorPage/components/TipsSidebar';
import InfoContent from './TaxDeductionCalculatorPage/components/InfoContent';

// Определение начального состояния формы
export const initialFormData = {
  incomeAmount: '1000000', // Годовой доход
  taxRate: '13', // Ставка НДФЛ (по умолчанию 13%)
  deductionType: 'property', // Тип вычета
  propertyPrice: '5000000', // Стоимость недвижимости
  mortgageInterest: '300000', // Сумма процентов по ипотеке
  includeInterestDeduction: false, // Вычет по процентам ипотеки
  medicalExpenses: '100000', // Расходы на лечение
  medicalExpensesType: 'standard', // Тип медицинских расходов
  educationExpenses: '100000', // Расходы на обучение
  educationType: 'self', // Тип обучения
  investmentAmount: '400000', // Сумма на ИИС
  investmentType: 'typeA', // Тип вычета по ИИС
  startYear: new Date().getFullYear(), // Год начала получения вычета
  includeSpouse: false, // Учитывать супруга(у) при имущественном вычете
  spouseIncome: '800000', // Годовой доход супруга(и)
  hasChildren: false, // Наличие детей для социальных вычетов
  childrenCount: '1', // Количество детей
  charityDonations: '50000', // Пожертвования
  additionalExpenses: [] // Доп. расходы для нестандартных вычетов
};

// Определение типов вычетов
export const deductionTypes = [
  { id: 'property', name: 'Имущественный вычет', maxAmount: 2000000, maxDeduction: 260000, description: 'Вычет при покупке жилья' },
  { id: 'mortgage', name: 'Вычет по ипотечным процентам', maxAmount: 3000000, maxDeduction: 390000, description: 'Вычет с процентов по ипотеке' },
  { id: 'medical', name: 'Социальный вычет (лечение)', maxAmount: 120000, maxDeduction: 15600, description: 'Вычет за медицинские услуги и лекарства' },
  { id: 'education', name: 'Социальный вычет (обучение)', maxAmount: 120000, maxDeduction: 15600, description: 'Вычет за образование' },
  { id: 'investment', name: 'Вычет по ИИС', maxAmount: 400000, maxDeduction: 52000, description: 'Вычет при инвестировании через ИИС' },
  { id: 'charity', name: 'Вычет на благотворительность', maxAmount: null, maxDeduction: null, description: 'Вычет при пожертвованиях' },
  { id: 'comprehensive', name: 'Комплексный расчет', maxAmount: null, maxDeduction: null, description: 'Расчет всех доступных вычетов' }
];

// Определение типов медицинских расходов
export const medicalExpensesTypes = [
  { id: 'standard', name: 'Обычное лечение', limit: 120000, description: 'Входит в общий лимит социальных вычетов' },
  { id: 'expensive', name: 'Дорогостоящее лечение', limit: null, description: 'Без ограничения суммы вычета' }
];

// Определение типов образовательных вычетов
export const educationTypes = [
  { id: 'self', name: 'Собственное обучение', limit: 120000, description: 'Входит в общий лимит социальных вычетов' },
  { id: 'children', name: 'Обучение детей', limit: 50000, description: 'До 50 000 ₽ на каждого ребенка' }
];

// Определение типов вычетов по ИИС
export const investmentTypes = [
  { id: 'typeA', name: 'Тип А (на взносы)', description: 'Вычет 13% от суммы взносов на ИИС' },
  { id: 'typeB', name: 'Тип Б (на доходы)', description: 'Освобождение от налога на доходы от инвестиций' }
];

/**
 * TaxDeductionCalculatorPage Component - Полный калькулятор налоговых вычетов с различными типами вычетов и параметрами.
 */
const TaxDeductionCalculatorPage = () => {
  const { subToolId } = useParams();
  const { darkMode, toggleDarkMode } = useTheme();

  // --- Данные инструмента ---
  const tool = useMemo(() => getToolById('tax-deduction-calculator'), []);
  const pageTitle = tool?.metaTitle || 'Калькулятор налоговых вычетов | Gensio';
  const metaDescription = tool?.metaDescription || 'Полный калькулятор налоговых вычетов онлайн: имущественный вычет, социальные вычеты (лечение, обучение), вычет по ИИС, расчет сроков возврата НДФЛ и оптимального порядка получения вычетов.';

  // --- Управление состоянием ---
  const [formData, setFormData] = useState(initialFormData);
  const [results, setResults] = useState(null);
  const [schedule, setSchedule] = useState([]);
  const [showFullSchedule, setShowFullSchedule] = useState(false);
  const [errors, setErrors] = useState({});
  const [isCalculating, setIsCalculating] = useState(false);
  const [savedCalculations, setSavedCalculations] = useState([]);

  // --- Обновление заголовка и мета-описания ---
  useEffect(() => {
    document.title = pageTitle;
    let metaTag = document.querySelector('meta[name="description"]');
    if (!metaTag) {
      metaTag = document.createElement('meta');
      metaTag.name = 'description';
      document.head.appendChild(metaTag);
    }
    metaTag.setAttribute('content', metaDescription);
  }, [pageTitle, metaDescription]);

  // --- Загрузка сохраненных расчетов из localStorage ---
  useEffect(() => {
    const savedCalcData = localStorage.getItem('savedTaxDeductions');
    if (savedCalcData) {
      try {
        setSavedCalculations(JSON.parse(savedCalcData));
      } catch (e) {
        console.error("Error loading saved calculations:", e);
      }
    }
  }, []);

  // --- Обработка subToolId для предварительной настройки формы ---
  useEffect(() => {
    if (!subToolId) return;

    setFormData(prev => {
      let newState = { ...initialFormData };

      switch (subToolId) {
        case 'property':
          newState.deductionType = 'property';
          break;
        case 'mortgage':
          newState.deductionType = 'mortgage';
          newState.includeInterestDeduction = true;
          break;
        case 'medical':
          newState.deductionType = 'medical';
          break;
        case 'education':
          newState.deductionType = 'education';
          break;
        case 'investment':
          newState.deductionType = 'investment';
          break;
        case 'charity':
          newState.deductionType = 'charity';
          break;
        case 'comprehensive':
          newState.deductionType = 'comprehensive';
          break;
        default:
          newState = { ...prev };
      }
      return newState;
    });

    setResults(null);
    setSchedule([]);
    setErrors({});
    setShowFullSchedule(false);

  }, [subToolId]);

  // Сохранение расчета в localStorage
  const saveCalculation = (name) => {
    if (!results) return;

    const newSavedCalc = {
      id: Date.now().toString(),
      name: name || `Вычет - ${getDeductionTypeNameById(formData.deductionType)}`,
      date: new Date().toISOString(),
      formData: { ...formData },
      results: { ...results },
      deductionType: formData.deductionType
    };

    const updatedSavedCalcs = [...savedCalculations, newSavedCalc];
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxDeductions', JSON.stringify(updatedSavedCalcs));

    return newSavedCalc;
  };

  // Удаление сохраненного расчета
  const deleteSavedCalculation = (id) => {
    const updatedSavedCalcs = savedCalculations.filter(calc => calc.id !== id);
    setSavedCalculations(updatedSavedCalcs);
    localStorage.setItem('savedTaxDeductions', JSON.stringify(updatedSavedCalcs));
  };

  // Загрузка сохраненного расчета
  const loadSavedCalculation = (id) => {
    const calcToLoad = savedCalculations.find(calc => calc.id === id);
    if (calcToLoad) {
      setFormData(calcToLoad.formData);
      setResults(calcToLoad.results);
      // Для корректной визуализации также можно восстановить график
      setSchedule(calcToLoad.schedule || []);
    }
  };

  // Вспомогательная функция для получения названия типа вычета
  const getDeductionTypeNameById = (typeId) => {
    const type = deductionTypes.find(t => t.id === typeId);
    return type ? type.name : 'Неизвестный вычет';
  };

  // --- RENDER ---
  return (
    <div className={`min-h-screen ${darkMode ? 'dark' : ''} bg-gray-50 dark:bg-gray-900 text-gray-900 dark:text-white transition-colors duration-200 font-sans`}>
      {/* Background Pattern */}
      <div aria-hidden="true" className="fixed inset-0 bg-[radial-gradient(theme(colors.gray.200)_1px,transparent_1px)] dark:bg-[radial-gradient(theme(colors.gray.800)_1px,transparent_1px)] [background-size:24px_24px] opacity-20 dark:opacity-10 pointer-events-none z-[-1]"></div>

      <Header darkMode={darkMode} toggleDarkMode={toggleDarkMode} />

      <main className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <Breadcrumbs />

        <div className="flex flex-col lg:flex-row gap-8 lg:gap-12">
          {/* --- Main Content Area --- */}
          <div className="lg:w-2/3 flex-shrink-0">
            {/* Page Header */}
            <div className="mb-8">
              <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-white mb-3 flex items-center gap-3">
                <PercentSquare className="w-8 h-8 text-emerald-600 dark:text-emerald-400 flex-shrink-0" />
                {tool?.name || 'Калькулятор налоговых вычетов'}
              </h1>
              <p className="text-lg text-gray-600 dark:text-gray-400">
                {metaDescription}
              </p>
            </div>

            {/* --- Форма калькулятора --- */}
            <CalculationForm 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              setErrors={setErrors}
              isCalculating={isCalculating}
              setIsCalculating={setIsCalculating}
              results={results}
              setResults={setResults}
              schedule={schedule}
              setSchedule={setSchedule}
              deductionTypes={deductionTypes}
              medicalExpensesTypes={medicalExpensesTypes}
              educationTypes={educationTypes}
              investmentTypes={investmentTypes}
              saveCalculation={saveCalculation}
            />

            {/* --- Результаты расчета --- */}
            {results && !isCalculating && (
              <>
                <ResultsOverview results={results} formData={formData} />
                <DeductionsSchedule 
                  schedule={schedule} 
                  results={results}
                  formData={formData}
                  showFullSchedule={showFullSchedule}
                  setShowFullSchedule={setShowFullSchedule}
                />
              </>
            )}

            {/* --- Информационный контент о налоговых вычетах --- */}
            <InfoContent />
          </div>

          {/* --- Sidebar Area --- */}
          <aside className="lg:w-1/3 self-start space-y-8">
            {/* --- Панель быстрой настройки --- */}
            <QuickAdjustmentSidebar 
              formData={formData}
              setFormData={setFormData}
              errors={errors}
              isCalculating={isCalculating}
              deductionTypes={deductionTypes}
            />

            {/* --- Панель сравнения расчетов --- */}
            <ComparisonSidebar 
              savedCalculations={savedCalculations}
              deleteSavedCalculation={deleteSavedCalculation}
              loadSavedCalculation={loadSavedCalculation}
            />

            {/* --- Панель полезных советов --- */}
            <TipsSidebar />

            {/* --- Связанные инструменты --- */}
            {tool && (
              <ToolSidebar 
                currentToolId={tool.id} 
                currentSubToolId={subToolId} 
                tool={tool} 
              />
            )}
          </aside>
        </div>
      </main>

      <Footer />

      {/* --- Global Styles --- */}
      <style jsx global>{`
        /* Base Input Styles */
        .form-input, .form-select, .form-textarea {
          @apply block w-full rounded-md border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 shadow-sm focus:border-emerald-500 focus:ring-emerald-500 sm:text-sm text-gray-900 dark:text-white transition duration-150 ease-in-out disabled:opacity-60 disabled:cursor-not-allowed dark:placeholder-gray-400;
        }
        .form-checkbox {
          @apply h-4 w-4 rounded border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        .form-radio {
          @apply h-4 w-4 border-gray-300 dark:border-gray-600 text-emerald-600 focus:ring-emerald-500 dark:bg-gray-700 cursor-pointer transition duration-150 ease-in-out;
        }
        /* Error State Styling */
        .input-error {
          @apply border-red-500 dark:border-red-500 text-red-900 dark:text-red-300 placeholder-red-400 dark:placeholder-red-600 focus:border-red-500 dark:focus:border-red-500 focus:ring-red-500 dark:focus:ring-red-500;
        }

        /* Custom Scrollbar for Deductions Schedule Table */
        .deductions-schedule-table {
          scrollbar-width: thin;
          scrollbar-color: theme('colors.gray.400') theme('colors.gray.100');
        }
        .dark .deductions-schedule-table {
          scrollbar-color: theme('colors.gray.600') theme('colors.gray.800');
        }
        .deductions-schedule-table::-webkit-scrollbar {
          width: 8px;
          height: 8px;
        }
        .deductions-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.100');
          border-radius: 10px;
        }
        .dark .deductions-schedule-table::-webkit-scrollbar-track {
          background-color: theme('colors.gray.800');
        }
        .deductions-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.400');
          border-radius: 10px;
          border: 2px solid theme('colors.gray.100');
        }
        .dark .deductions-schedule-table::-webkit-scrollbar-thumb {
          background-color: theme('colors.gray.600');
          border-color: theme('colors.gray.800');
        }
        .deductions-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
        .dark .deductions-schedule-table::-webkit-scrollbar-thumb:hover {
          background-color: theme('colors.gray.500');
        }
      `}</style>
    </div>
  );
};

export default TaxDeductionCalculatorPage;